import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSavingsEngine, getAnalystBrief, exportSavingsCSV } from '../services/api';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Download, ChevronRight, ChevronDown, Sparkles, Loader2,
  Target, Clock, AlertTriangle, CheckCircle2, Shield, Send,
  DollarSign, BarChart3, Zap, ExternalLink, Activity, Filter,
  Database, FileText, Globe, Lock, Unlock
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// ── Currency formatter (€) ──────────────────────────────────

function fmt(v) {
  if (!v && v !== 0) return '€0';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}€${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}€${(abs / 1e3).toFixed(1)}K`;
  return `${sign}€${abs.toFixed(0)}`;
}

// ── Status Config ───────────────────────────────────────────

const STATUS = {
  executed: { label: 'Executed', color: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', Icon: CheckCircle2 },
  approved: { label: 'Approved', color: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', Icon: Shield },
  pending:  { label: 'Pending',  color: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', Icon: Clock },
  missed:   { label: 'Missed',   color: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', Icon: AlertTriangle },
};

// ── Confidence ──────────────────────────────────────────────

function ConfDot({ level }) {
  const c = level === 'high' ? 'bg-emerald-500' : level === 'moderate' ? 'bg-amber-500' : 'bg-red-500';
  const l = level === 'high' ? 'High' : level === 'moderate' ? 'Med' : 'Low';
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-1.5 h-1.5 rounded-full ${c}`} />
      <span className="text-[10px] font-semibold text-slate-500 uppercase">{l}</span>
    </span>
  );
}

// ── AI Analyst Banner ───────────────────────────────────────

function AnalystBanner({ insight, loading, onGenerate }) {
  return (
    <div className="bg-[#0A2540] rounded-2xl p-5 mb-6 relative overflow-hidden" data-testid="ai-analyst-banner">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-white/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">What Your Analyst Sees</span>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin text-white/60" /><span className="text-sm text-white/60">Analyzing your savings data...</span></div>
        ) : insight ? (
          <div className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto pr-4 custom-scrollbar">{insight}</div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-white/50">Generate AI-powered insights from your savings data</p>
            <button onClick={onGenerate} className="px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors" data-testid="generate-insight-btn">Generate Insight</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Start Here CTA ──────────────────────────────────────────

function StartHereBanner({ data }) {
  if (!data) return null;
  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5 mb-6 flex items-center justify-between" data-testid="start-here-banner">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <Target className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Start Here</span>
            <ChevronRight className="w-3 h-3 text-emerald-500" />
            <span className="text-lg font-black text-emerald-800">{fmt(data.savings)}</span>
            <span className="text-xs text-emerald-600">savings</span>
          </div>
          <p className="text-sm text-slate-700">
            <span className="font-semibold">{data.brand_name}</span>
            <span className="text-slate-400 mx-1.5">→</span>
            <span className="font-semibold">{data.generic_name}</span>
            <span className="text-xs text-slate-400 ml-2">({data.manufacturer})</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link to={`/drug/${data.drug_id}`} className="px-4 py-2 bg-white border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-lg hover:bg-emerald-50 transition-colors flex items-center gap-1.5" data-testid="start-here-review">
          <Send className="w-3.5 h-3.5" /> Send to Review
        </Link>
        <Link to={`/drug/${data.drug_id}`} className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5" data-testid="start-here-approve">
          <CheckCircle2 className="w-3.5 h-3.5" /> Approve
        </Link>
      </div>
    </div>
  );
}

// ── Summary Card ────────────────────────────────────────────

function SummaryCard({ title, value, sub, accent, icon: Icon, change, changeLabel }) {
  const isPositive = change >= 0;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 relative overflow-hidden" data-testid={`summary-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-1/2 translate-x-1/2 opacity-10 ${accent}`} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{title}</span>
        </div>
        <p className="text-3xl font-black text-slate-900 tracking-tight">{fmt(value)}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-400">{sub}</span>
          {change !== undefined && change !== null && (
            <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${isPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
              {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {changeLabel || `${Math.abs(change)}%`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Missed Yesterday ────────────────────────────────────────

function MissedYesterday({ data }) {
  if (!data || data.yesterday === 0) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6" data-testid="missed-yesterday">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Yesterday You Missed</span>
          </div>
          <p className="text-3xl font-black text-red-700">{fmt(data.yesterday)}</p>
        </div>
        <div className="text-right space-y-1">
          <div><span className="text-xs text-red-400">This week</span> <span className="text-sm font-bold text-red-700">{fmt(data.this_week)}</span></div>
          <div><span className="text-xs text-red-400">This month</span> <span className="text-sm font-bold text-red-700">{fmt(data.this_month)}</span></div>
        </div>
      </div>
      <div className="flex gap-3 mt-3">
        {data.drivers.map((d, i) => (
          <div key={i} className="flex-1 bg-white border border-red-100 rounded-lg p-2.5">
            <p className="text-xs font-semibold text-slate-800 truncate">{d.drug}</p>
            <p className="text-sm font-bold text-red-600">{fmt(d.daily_loss)}/day</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Savings Timeline Chart ──────────────────────────────────

function SavingsTimeline({ data }) {
  if (!data || data.length === 0) return null;
  const chartData = data.map(d => ({
    period: d._id,
    Actual: d.actual,
    Projected: d.projected,
  }));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="savings-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#0A2540]" />
          <h2 className="text-sm font-bold text-slate-900">Savings Timeline</h2>
        </div>
        <div className="flex items-center gap-4 text-[10px]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-emerald-500 rounded-full" /> Actual</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-slate-300 rounded-full border border-dashed border-slate-400" /> Projected</span>
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <defs>
              <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={60} />
            <Tooltip formatter={v => fmt(v)} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
            <Area type="monotone" dataKey="Projected" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="6 3" fill="none" dot={false} />
            <Area type="monotone" dataKey="Actual" stroke="#10b981" strokeWidth={2.5} fill="url(#actualGrad)" dot={{ fill: '#10b981', r: 3, strokeWidth: 0 }} activeDot={{ r: 5, fill: '#10b981' }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Expanded Row Detail ─────────────────────────────────────

function RowDetail({ entry }) {
  const scores = entry.scores || {};
  const be = entry.bioequivalence || {};
  const regEntries = entry.regulatory_entries || [];
  const scoreKeys = [
    { key: 'efficacy', label: 'Efficacy' },
    { key: 'regulatory', label: 'Regulatory' },
    { key: 'availability', label: 'Availability' },
    { key: 'manufacturing', label: 'Manufacturing' },
  ];

  return (
    <tr data-testid={`row-detail-${entry.id}`}>
      <td colSpan={7} className="p-0">
        <div className="bg-slate-50/80 border-t border-b border-slate-100 px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {/* Savings Calculation */}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-2">Savings Calculation</p>
              <div className="space-y-1.5">
                <div className="flex justify-between"><span className="text-xs text-slate-500">Projected</span><span className="text-xs font-semibold">{fmt(entry.projected)}</span></div>
                <div className="flex justify-between"><span className="text-xs text-slate-500">Actual</span><span className="text-xs font-bold text-emerald-600">{fmt(entry.savings)}</span></div>
                {entry.variance !== 0 && (
                  <div className="flex justify-between"><span className="text-xs text-slate-500">Variance</span><span className={`text-xs font-bold ${entry.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{entry.variance >= 0 ? '+' : ''}{fmt(entry.variance)}</span></div>
                )}
                {entry.units > 0 && <div className="flex justify-between"><span className="text-xs text-slate-500">Units switched</span><span className="text-xs font-semibold">{entry.units.toLocaleString()}</span></div>}
              </div>
            </div>

            {/* Regulatory Validation */}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-2">Regulatory Validation</p>
              {regEntries.length > 0 ? (
                <div className="space-y-1">
                  {regEntries.slice(0, 4).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={`w-1.5 h-1.5 rounded-full ${r.status === 'approved' || r.status === 'interchangeable' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                      <span className="font-medium text-slate-700">{r.region}</span>
                      <span className="text-[10px] text-slate-400">{r.source_name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">No regulatory data</p>
              )}
              {be.rating && (
                <div className="mt-2 px-2 py-1 bg-white rounded border border-slate-200 inline-block">
                  <span className="text-[10px] text-slate-400">BE: </span>
                  <span className="text-[10px] font-bold text-slate-700">{be.rating}</span>
                </div>
              )}
            </div>

            {/* Scores */}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-2">Quality Scores</p>
              <div className="space-y-1.5">
                {scoreKeys.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400 w-16">{label}</span>
                    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${(scores[key] || 0) >= 85 ? 'bg-emerald-500' : (scores[key] || 0) >= 70 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${scores[key] || 0}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-slate-600 w-6 text-right">{scores[key] || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Flags */}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-2">Risk Flags</p>
              {entry.risk_flags && entry.risk_flags.length > 0 ? (
                <div className="space-y-1">
                  {entry.risk_flags.map((f, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-100">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>No risk flags identified</span>
                </div>
              )}
              <Link to={`/drug/${entry.drug_id}`} className="mt-3 inline-flex items-center gap-1 text-xs text-[#0A2540] font-semibold hover:underline">
                View full detail <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Drivers by Area ─────────────────────────────────────────

function DriversPanel({ drivers }) {
  if (!drivers || drivers.length === 0) return null;
  const maxSavings = Math.max(...drivers.map(d => d.total_savings));
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6" data-testid="drivers-by-area">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-[#0A2540]" />
        <h2 className="text-sm font-bold text-slate-900">Savings by Therapeutic Area</h2>
      </div>
      <div className="space-y-3">
        {drivers.map((d, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-500 w-24 text-right truncate">{d.area}</span>
            <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden relative">
              <div className="h-full bg-[#0A2540] rounded-full transition-all duration-700" style={{ width: `${(d.total_savings / maxSavings) * 100}%` }} />
              <span className="absolute inset-y-0 right-2 flex items-center text-[10px] font-bold text-slate-500">{d.pct}%</span>
            </div>
            <span className="text-xs font-bold text-slate-800 w-16 text-right">{fmt(d.total_savings)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MAIN PAGE ───────────────────────────────────────────────

export default function SavingsLedgerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await getSavingsEngine();
        setData(res.data);
      } catch (err) {
        console.error('Failed to load savings engine:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleGenerateInsight = async () => {
    setLoadingAI(true);
    try {
      const res = await getAnalystBrief(null, 'cfo');
      setAiInsight(res.data.brief);
    } catch {
      toast.error('Failed to generate insight');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportSavingsCSV();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Savings_Report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Savings report exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="savings-loading">
        <div className="skeleton h-8 w-64 mb-6" />
        <div className="skeleton h-20 rounded-2xl mb-6" />
        <div className="grid grid-cols-4 gap-4 mb-6">{[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
        <div className="skeleton h-56 rounded-2xl mb-6" />
        <div className="skeleton h-64 rounded-2xl" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-center text-slate-500">Failed to load savings data</div>;
  }

  const { summary, timeline, ledger, start_here, missed_yesterday, drivers_by_area, departments } = data;

  // Apply filters
  let filtered = ledger;
  if (statusFilter !== 'all') filtered = filtered.filter(e => e.status === statusFilter);
  if (deptFilter !== 'all') filtered = filtered.filter(e => e.department === deptFilter);

  const statusCounts = { all: ledger.length, executed: 0, approved: 0, pending: 0, missed: 0 };
  ledger.forEach(e => { if (statusCounts[e.status] !== undefined) statusCounts[e.status]++; });

  return (
    <div className="p-6" data-testid="savings-ledger-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Savings Engine</h1>
          <p className="text-sm text-slate-500 mt-0.5">Your hospital's financial recovery ledger</p>
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-testid="export-btn">
          <Download className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export Savings Report'}
        </button>
      </div>

      {/* AI Analyst */}
      <AnalystBanner insight={aiInsight} loading={loadingAI} onGenerate={handleGenerateInsight} />

      {/* Start Here */}
      <StartHereBanner data={start_here} />

      {/* 4 Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="summary-cards">
        <SummaryCard
          title="Saved This Month"
          value={summary.this_month.actual}
          sub={`${summary.this_month.count} entries`}
          accent="bg-emerald-500"
          icon={DollarSign}
          change={summary.this_month.change_pct}
          changeLabel={`${summary.this_month.change_pct > 0 ? '+' : ''}${summary.this_month.change_pct}% vs last month`}
        />
        <SummaryCard
          title="Saved YTD"
          value={summary.ytd.actual}
          sub={`${summary.ytd.count} executed actions`}
          accent="bg-[#0A2540]"
          icon={TrendingUp}
        />
        <SummaryCard
          title="Pipeline Value"
          value={summary.pipeline.value}
          sub={`${summary.pipeline.count} approved, not executed`}
          accent="bg-blue-500"
          icon={Zap}
        />
        <SummaryCard
          title="Missed Opportunities"
          value={summary.missed.value}
          sub={`${summary.missed.count} items, last 30 days`}
          accent="bg-red-500"
          icon={AlertTriangle}
        />
      </div>

      {/* Timeline */}
      <SavingsTimeline data={timeline} />

      {/* Missed Yesterday */}
      <MissedYesterday data={missed_yesterday} />

      {/* Filters */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2" data-testid="status-filters">
          {['all', 'executed', 'approved', 'pending', 'missed'].map(key => {
            const isActive = statusFilter === key;
            const cfg = STATUS[key] || {};
            return (
              <button key={key} onClick={() => setStatusFilter(key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${isActive ? 'bg-[#0A2540] text-white shadow-sm' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}`}
                data-testid={`filter-${key}`}>
                {key === 'all' ? `All (${statusCounts.all})` : `${cfg.label || key} (${statusCounts[key]})`}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white focus:outline-none focus:ring-1 focus:ring-[#0A2540]" data-testid="dept-filter">
            <option value="all">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6" data-testid="ledger-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Date</th>
                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Switch</th>
                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Department</th>
                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Savings</th>
                <th className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Confidence</th>
                <th className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => {
                const cfg = STATUS[entry.status] || STATUS.pending;
                const StatusIcon = cfg.Icon;
                const isExpanded = expandedRow === entry.id;

                return (
                  <React.Fragment key={entry.id}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : entry.id)}
                      className={`border-b border-slate-50 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                      data-testid={`ledger-row-${entry.id}`}
                    >
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-slate-500">{entry.date}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className="text-sm font-semibold text-slate-900">{entry.brand_name}</span>
                          <span className="text-slate-300 mx-1">→</span>
                          <span className="text-sm font-medium text-emerald-700">{entry.generic_name}</span>
                        </div>
                        <span className="text-[10px] text-slate-400">{entry.manufacturer}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-600">{entry.department}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                          <StatusIcon className="w-2.5 h-2.5" />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-bold ${entry.status === 'missed' ? 'text-red-600' : 'text-slate-900'}`}>
                          {fmt(entry.savings)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <ConfDot level={entry.confidence_level} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </td>
                    </tr>
                    {isExpanded && <RowDetail entry={entry} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="py-16 text-center">
            <Shield className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No entries match the selected filters</p>
          </div>
        )}
      </div>

      {/* Drivers by Area */}
      <DriversPanel drivers={drivers_by_area} />

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-[10px] text-slate-400">
          All savings figures are computed from deterministic analysis. AI is used for insight summarization only.
          Pipeline values are estimates based on current generic pricing and adoption projections.
        </p>
      </div>
    </div>
  );
}
